import { useContext, useEffect, useMemo, useState } from "react";
import {
    ApiContext,
    CheckoutActionTypes,
    CheckoutContext,
} from "@swiggy-private/checkout-core";
import { PaymentFallbacksApiResponse, PaymentMethod, PaymentMethodMeta } from "@swiggy-private/payment-ui";
import { UpiIntentContext } from "@payment-client/contexts/UpiIntentContext";
import { isEmpty } from "@swiggy-private/core-ui";
import { getBusinessLine } from "@payment-client/helpers/Misc";
import { getPaymentFallbackMethodsPublic } from "@payment-client/apis/publicPayment";

const validateAndFilterPaymentfallbacks = (
    fallbackApiResponse: PaymentFallbacksApiResponse,
    availableUpiIntentMethodsInDevice: string[],
): PaymentMethod<PaymentMethodMeta>[] => {
    let availableUpiIntentMethods: PaymentMethod<PaymentMethodMeta>[] = [];
    if (fallbackApiResponse.recommendedUpiIntentMethods) {
        availableUpiIntentMethods = fallbackApiResponse.recommendedUpiIntentMethods.filter(method =>
            availableUpiIntentMethodsInDevice.includes(method.name),
        );
    }

    // special handling when there are only PM in fallback list and there are methods present in recommended upi list
    if (fallbackApiResponse.paymentMethods.length === 0 && !isEmpty(availableUpiIntentMethods)) {
        return [availableUpiIntentMethods[0]];
    }

    // special handling when there is only PM in fallback list and there are methods present in recommended upi list
    if (
        fallbackApiResponse.paymentMethods.length === 1 &&
        fallbackApiResponse.paymentMethods[0].lastUsedPm &&
        !isEmpty(availableUpiIntentMethods)
    ) {
        if (fallbackApiResponse.couponApplied) {
            // if coupon is applied, first method is coupon applicable one
            if (fallbackApiResponse.paymentMethods[0].couponApplicable) {
                fallbackApiResponse.paymentMethods.push(availableUpiIntentMethods[0]);
            }
        } else {
            // if coupon is not applied first method is recommended one
            fallbackApiResponse.paymentMethods.unshift(availableUpiIntentMethods[0]);
        }
        return fallbackApiResponse.paymentMethods;
    }

    // case when there are 2 methods in fallback list but there are methods available in recommended upi list.
    // in that case we will replace the recommended methods with available upi intent one
    const filteredmethods = fallbackApiResponse.paymentMethods.map(fallbackMethod => {
        if (fallbackMethod?.lastUsedPm === false && !isEmpty(availableUpiIntentMethods)) {
            fallbackMethod = availableUpiIntentMethods[0];
        }
        return fallbackMethod;
    });
    return filteredmethods;
};

const UsePaymentFallbacks = (): {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fallbackApiMethods: any;
    isFallbackApiInProgress: boolean;
} => {
    const { checkoutState } = useContext(CheckoutContext);
    const apiContextValue = useContext(ApiContext);
    const { availableMethods: availableUpiIntentMethodsInDevice } = useContext(UpiIntentContext);
    const apiHost = apiContextValue.host?.unwrap();
    const [fallbackApiMethods, setFallbackApiMethods] = useState<PaymentFallbacksApiResponse>();
    const [isFallbackApiInProgress, setIsFallbackApiInProgress] = useState(false);
    const businessLine = useMemo(() => getBusinessLine(), []);

    const getFallbackMethods = getPaymentFallbackMethodsPublic;

    useEffect(() => {
        if (
            checkoutState.type === CheckoutActionTypes.PAYMENT_FAILED &&
            checkoutState?.payload?.isFallbackApiEnabled &&
            checkoutState?.payload?.failedOrder?.orderId
        ) {
            setIsFallbackApiInProgress(true);

            let orderOrTransactionId;
            const failedOrderData = checkoutState?.payload?.failedOrder;

            if (failedOrderData != null) {
                orderOrTransactionId = failedOrderData.paymentTxnId;
            }

            getFallbackMethods(apiHost, String(orderOrTransactionId))
                .then(data => {
                    if (data && data.paymentMethods) {
                        data.paymentMethods = validateAndFilterPaymentfallbacks(
                            data,
                            availableUpiIntentMethodsInDevice,
                        );
                        setFallbackApiMethods(data);
                    }
                    setIsFallbackApiInProgress(false);
                })
                .catch(() => {
                    setIsFallbackApiInProgress(false);
                });
        }
    }, [apiHost, checkoutState, availableUpiIntentMethodsInDevice, businessLine, getFallbackMethods]);

    return {
        fallbackApiMethods,
        isFallbackApiInProgress,
    };
};

export default UsePaymentFallbacks;
